import { z } from "zod";
import { Alertas, Especie, EspecieOuTodas, Perfil, Refs } from "./base.ts";

// Uma ficha por item em módulos de lista; um documento só nos demais.
// `.loose()` mantém campos novos que o admin ainda não conhece em vez de apagá-los.

const especies = z.array(EspecieOuTodas).min(1);
const Apres = z.object({ rotulo: z.string(), mgMl: z.number().optional(), uMl: z.number().optional() });

export const Dose = z
  .object({
    id: z.string(),
    especie: Especie,
    farmaco: z.string(),
    classe: z.string().optional(),
    via: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    un: z.string().optional(),
    intervalo: z.string().optional(),
    duracao: z.string().optional(),
    contexto: z.string().optional(),
    semDose: z.boolean().optional(),
    controle: z.string().optional(),
    controleNota: z.string().optional(),
    controleRefs: Refs.optional(),
    apres: z.array(Apres).optional(),
    alertas: Alertas.optional(),
    /** Id da monografia no bulário, quando existe. */
    bula: z.string().optional(),
    refs: Refs,
  })
  .loose()
  .refine((d) => d.semDose || (d.min != null && !!d.un), { message: "dose sem min e un precisa de semDose" });
export type Dose = z.infer<typeof Dose>;

/** Trecho de bulário: cada afirmação leva a própria fonte. */
const TrechoBula = z.object({ texto: z.string().min(1), refs: Refs.min(1) }).strict();

export const CONDUTAS_INTERACAO = ["evitar", "cautela", "monitorar"] as const;

/**
 * Monografia de um fármaco, no formato de bulário: apresentações, indicações,
 * contraindicações, mecanismo, interações e efeitos adversos. As doses não
 * moram aqui: vêm das fichas de `doses` que apontam para esta (`bula`), que já
 * têm cálculo por peso e vão para a receita.
 */
export const Bula = z
  .object({
    id: z.string().regex(/^[a-z0-9-]+$/, "id em minúsculas com hífen, como clemastina"),
    farmaco: z.string(),
    classe: z.string(),
    especies: especies,
    /** Uma linha que diz para que serve, no topo da folha. */
    resumo: z.string().optional(),
    apresentacoes: z
      .array(
        z
          .object({
            nome: z.string(),
            /** Forma, concentração e uso ficam em branco quando a fonte só traz a linha do produto. */
            forma: z.string().optional(),
            concentracao: z.string().optional(),
            uso: z.enum(["humano", "veterinario"]).optional(),
            fabricante: z.string().optional(),
            refs: Refs.min(1),
          })
          .strict(),
      )
      .optional(),
    indicacoes: z.array(TrechoBula).optional(),
    contraindicacoes: z.array(TrechoBula).optional(),
    mecanismo: TrechoBula.optional(),
    interacoes: z
      .array(z.object({ agente: z.string(), efeito: z.string(), conduta: z.enum(CONDUTAS_INTERACAO), refs: Refs.min(1) }).strict())
      .optional(),
    efeitos: z.array(z.object({ especie: EspecieOuTodas, texto: z.string(), refs: Refs.min(1) }).strict()).optional(),
    alertas: Alertas.optional(),
    refs: Refs,
  })
  .loose();
export type Bula = z.infer<typeof Bula>;

export const Limiar = z.object({
  especie: EspecieOuTodas,
  valor: z.number().optional(),
  un: z.string().optional(),
  texto: z.string(),
  nivel: z.enum(["leve", "grave"]).optional(),
  refs: Refs.optional(),
});
export type Limiar = z.infer<typeof Limiar>;

export const Toxico = z
  .object({
    id: z.string(),
    cat: z.enum(["alimento", "humano", "planta", "praguicida", "animal", "outros"]),
    agente: z.string(),
    sinonimos: z.string().optional(),
    especies: especies.optional(),
    resumo: z.string().optional(),
    limiares: z.array(Limiar).optional(),
    calc: z
      .object({
        entrada: z.string(),
        exemplo: z.number().optional(),
        un: z.string().optional(),
        fatores: z.array(z.object({ rotulo: z.string(), fator: z.number() })).optional(),
        refs: Refs.optional(),
      })
      .optional(),
    sinais: z.string().optional(),
    conduta: z.string().optional(),
    antidotos: z.array(z.object({ nome: z.string(), dose: z.string(), especie: Especie.optional(), refs: Refs.optional() })).optional(),
    semLimiar: z.string().optional(),
    alertas: Alertas.optional(),
    refs: Refs,
  })
  .loose();
export type Toxico = z.infer<typeof Toxico>;

export const Produto = z
  .object({
    id: z.string(),
    produto: z.string(),
    farmaco: z.string(),
    fabricante: z.string().optional(),
    especies: especies,
    concentracao: z.string().optional(),
    carenciaCarne: z.string().nullish(),
    carenciaLeite: z.string().nullish(),
    leiteTexto: z.string().optional(),
    registro: z.string().optional(),
    dose: z.string().optional(),
    via: z.string().optional(),
    secundaria: z.boolean().optional(),
    alertas: Alertas.optional(),
    refs: Refs,
  })
  .loose();
export type Produto = z.infer<typeof Produto>;

export const Planta = z
  .object({
    id: z.string(),
    nome: z.string(),
    cientifico: z.string().optional(),
    especies: especies,
    efeito: z.string().optional(),
    dl: z.string().optional(),
    dlRefs: Refs.optional(),
    nota: z.string().optional(),
    alertas: Alertas.optional(),
    refs: Refs,
  })
  .loose();
export type Planta = z.infer<typeof Planta>;

export const Vacina = z
  .object({ id: z.string(), especies: especies, titulo: z.string(), tipo: z.string().optional(), itens: z.array(z.string()), refs: Refs })
  .loose();
export type Vacina = z.infer<typeof Vacina>;

export const Regra = z
  .object({
    id: z.string(),
    publico: z.enum(["vet", "farmacia", "agro", "campo"]),
    titulo: z.string(),
    resumo: z.string().optional(),
    itens: z.array(z.object({ t: z.string(), refs: Refs.optional() })).optional(),
    especies: especies.optional(),
    nota: z.string().optional(),
    refs: Refs,
  })
  .loose();
export type Regra = z.infer<typeof Regra>;

export const Formula = z
  .object({
    id: z.string(),
    calc: z.boolean().optional(),
    curto: z.string().optional(),
    nome: z.string(),
    expr: z.string(),
    constantes: z.record(z.string(), z.unknown()).optional(),
    nota: z.string().optional(),
    refs: Refs,
  })
  .loose();
export type Formula = z.infer<typeof Formula>;

export const Referencia = z
  .object({ especie: Especie, rotulo: z.string().optional(), temp: z.string(), fc: z.string(), fr: z.string().nullable(), refs: Refs })
  .loose();
export type Referencia = z.infer<typeof Referencia>;

export const Laboratorio = z
  .object({ analito: z.string(), cao: z.string().optional(), gato: z.string().optional(), un: z.string().optional(), refs: Refs })
  .loose();
export type Laboratorio = z.infer<typeof Laboratorio>;

export const Questao = z
  .object({
    id: z.string(),
    q: z.string(),
    opcoes: z.array(z.string()).min(2),
    correta: z.number().int().min(0),
    explicacao: z.string(),
    refs: Refs,
  })
  .loose()
  .refine((q) => q.correta < q.opcoes.length, { message: "correta fora das opções" });
export type Questao = z.infer<typeof Questao>;

export const Escala = z
  .object({
    id: z.string(),
    especies: especies,
    nome: z.string(),
    faixa: z.string().optional(),
    corte: z.string().optional(),
    nota: z.string().optional(),
    refs: Refs,
  })
  .loose();
export type Escala = z.infer<typeof Escala>;

export const Cenario = z
  .object({
    titulo: z.string(),
    desc: z.string(),
    especie: Especie,
    perfil: Perfil,
    peso: z.number().optional(),
    tab: z.string(),
    sub: z.string().optional(),
    calc: z.string().optional(),
    sheet: z.object({ t: z.string(), id: z.string() }).optional(),
    toxIn: z.object({ qtd: z.number().optional(), tipo: z.number().optional() }).optional(),
  })
  .loose();
export type Cenario = z.infer<typeof Cenario>;

export const DrogaEmergencia = z
  .object({
    id: z.string(),
    farmaco: z.string(),
    uso: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    un: z.string(),
    via: z.string().optional(),
    texto: z.string().optional(),
    especies: especies.optional(),
    apres: z.array(Apres).optional(),
    refs: Refs,
  })
  .loose();
export type DrogaEmergencia = z.infer<typeof DrogaEmergencia>;

export const Emergencia = z
  .object({
    rcp: z.array(z.object({ rotulo: z.string(), valor: z.string(), refs: Refs })),
    drogas: z.array(DrogaEmergencia),
    alertas: Alertas.optional(),
  })
  .loose();
export type Emergencia = z.infer<typeof Emergencia>;

export const Mdr1 = z
  .object({
    resumo: z.string(),
    farmacos: z.array(z.object({ nome: z.string(), cat: z.string(), nota: z.string().optional(), refs: Refs.optional() }).loose()),
    racas: z.array(z.object({ raca: z.string(), wsu: z.string().optional(), br: z.string().optional() }).loose()),
    racasRefs: Refs.optional(),
    gato: z.object({ texto: z.string(), refs: Refs.optional() }).optional(),
    refs: Refs,
  })
  .loose();
export type Mdr1 = z.infer<typeof Mdr1>;

export const Receita = z.object({ campos: z.array(z.object({ t: z.string(), chave: z.string().optional() })), refs: Refs }).loose();
export type Receita = z.infer<typeof Receita>;

const TextoComFonte = z.object({ texto: z.string(), refs: Refs });
export const Campo = z.object({ panorama: TextoComFonte, carencia: TextoComFonte }).loose();
export type Campo = z.infer<typeof Campo>;

export const Meta = z.object({ conferidoEm: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), versao: z.string() }).loose();
export type Meta = z.infer<typeof Meta>;

const ListaTextos = z.array(z.string());

export const slug = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export type DefModulo = {
  schema: z.ZodType;
  /** Lista: uma ficha por item. Documento: a coleção inteira é uma ficha. */
  tipo: "lista" | "doc";
  /** Item clínico exige ao menos uma ref. Num documento, os caminhos das listas internas que exigem. */
  exigeRef?: true | readonly string[];
  /** Id estável do item numa lista. */
  // biome-ignore lint/suspicious/noExplicitAny: cada módulo tem a própria forma
  id?: (x: any) => string;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  titulo: (x: any) => string;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  especies?: (x: any) => string[];
  /** Material interno: não vai para a release do app. */
  interno?: boolean;
};

const esp = (x: { especies?: string[] }) => x.especies ?? [];

export const MODULOS = {
  doses: { schema: Dose, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.farmaco, especies: (x) => [x.especie] },
  bulario: { schema: Bula, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.farmaco, especies: esp },
  toxicos: { schema: Toxico, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.agente, especies: esp },
  produtos: { schema: Produto, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.produto, especies: esp },
  plantas: { schema: Planta, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.nome, especies: esp },
  vacinas: { schema: Vacina, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.titulo, especies: esp },
  regras: { schema: Regra, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.titulo, especies: esp },
  formulas: {
    schema: Formula,
    tipo: "lista",
    exigeRef: true,
    id: (x) => x.id,
    titulo: (x) => x.nome,
    especies: (x) => Object.keys(x.constantes ?? {}),
  },
  referencia: {
    schema: Referencia,
    tipo: "lista",
    exigeRef: true,
    id: (x) => slug(`${x.especie} ${x.rotulo ?? ""}`),
    titulo: (x) => (x.rotulo ? `${x.especie} · ${x.rotulo}` : x.especie),
    especies: (x) => [x.especie],
  },
  laboratorio: {
    schema: Laboratorio,
    tipo: "lista",
    exigeRef: true,
    id: (x) => slug(x.analito),
    titulo: (x) => x.analito,
    especies: () => ["cao", "gato"],
  },
  questoes: { schema: Questao, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.q },
  escalas: { schema: Escala, tipo: "lista", exigeRef: true, id: (x) => x.id, titulo: (x) => x.nome, especies: esp },
  cenarios: { schema: Cenario, tipo: "lista", id: (x) => slug(x.titulo), titulo: (x) => x.titulo, especies: (x) => [x.especie] },
  emergencia: {
    schema: Emergencia,
    tipo: "doc",
    exigeRef: ["drogas", "rcp"],
    titulo: () => "Emergência · RCP",
    especies: () => ["cao", "gato"],
  },
  mdr1: { schema: Mdr1, tipo: "doc", titulo: () => "MDR1 e raças", especies: () => ["cao", "gato"] },
  receita: { schema: Receita, tipo: "doc", titulo: () => "Campos da receita" },
  campo: { schema: Campo, tipo: "doc", titulo: () => "Panorama do campo", especies: () => ["bovino", "equino"] },
  meta: { schema: Meta, tipo: "doc", titulo: () => "Metadados da base" },
  mercado: { schema: z.record(z.string(), z.unknown()), tipo: "doc", titulo: () => "Mercado", interno: true },
  auditoria: { schema: ListaTextos, tipo: "doc", titulo: () => "Como auditamos" },
  lacunas: { schema: ListaTextos, tipo: "doc", titulo: () => "Lacunas conhecidas" },
} as const satisfies Record<string, DefModulo>;

export type Modulo = keyof typeof MODULOS;
export const NOMES_MODULOS = Object.keys(MODULOS) as Modulo[];
export const ModuloEnum = z.enum(NOMES_MODULOS as [Modulo, ...Modulo[]]);
export const moduloDef = (m: Modulo): DefModulo => MODULOS[m];
export const ehModulo = (m: string): m is Modulo => m in MODULOS;
